import { useState } from 'react';
import { i18n } from '@lingui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Box, MenuItem, TextField, Typography } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import {
    AGENT_OS_DOCKER_MAC,
    AGENT_OS_LINUX,
    AGENT_OS_MAC,
    AGENT_OS_WINDOWS,
    AGENT_VIRTUAL_2K_MODEL,
    AGENT_VIRTUAL_3K_MODEL,
    AGENT_VIRTUAL_290_MODEL,
} from './AgentConstants';
import { useHelpUrl } from '../../../utilities/HelpUtilities';

const fieldLabel = 'Host Operating System';

const useStyles = makeStyles(() => ({
    root: {
        '& .MuiSelect-select': {
            width: 250,
        },
    },
}));

const AGENT_REQUIREMENTS = {
    [AGENT_OS_WINDOWS]: {
        [AGENT_VIRTUAL_2K_MODEL]: t`The Microsoft® Windows host needs at least 2GB RAM and 4GB free disk space`,
        [AGENT_VIRTUAL_3K_MODEL]: t`The Microsoft® Windows host needs at least 4GB RAM and 4GB free disk space`,
        [AGENT_VIRTUAL_290_MODEL]: t`The Microsoft® Windows host needs at least 4GB RAM and 4GB free disk space`,
    },
    [AGENT_OS_LINUX]: {
        [AGENT_VIRTUAL_2K_MODEL]: t`The Linux host needs at least 2GB RAM and 1GB free disk space`,
        [AGENT_VIRTUAL_3K_MODEL]: t`The Linux host needs at least 4GB RAM and 2GB free disk space`,
        [AGENT_VIRTUAL_290_MODEL]: t`The Linux host needs at least 4GB RAM and 2GB free disk space`,
    },
    [AGENT_OS_MAC]: {
        [AGENT_VIRTUAL_2K_MODEL]: t`The Mac needs at least 4GB RAM and 2GB free disk space`,
        [AGENT_VIRTUAL_3K_MODEL]: t`The Mac needs at least 4GB RAM and 2GB free disk space`,
        [AGENT_VIRTUAL_290_MODEL]: t`The Mac needs at least 4GB RAM and 2GB free disk space`,
    },
    [AGENT_OS_DOCKER_MAC]: {
        [AGENT_VIRTUAL_2K_MODEL]: t`The Mac needs at least 2GB RAM and 4GB free disk space`,
        [AGENT_VIRTUAL_3K_MODEL]: t`The Mac needs at least 4GB RAM and 4GB free disk space`,
        [AGENT_VIRTUAL_290_MODEL]: t`The Mac needs at least 4GB RAM and 4GB free disk space`,
    },
};

const AGENT_DESCRIPTIONS = {
    [AGENT_OS_WINDOWS]: () => (
        <Trans>nPoint software can run on Microsoft&reg; Windows 10, Windows 11, Server 2019, and Server 2022</Trans>
    ),
    [AGENT_OS_LINUX]: () => (
        <Trans>
            nPoint software is supported on most 64-bit versions of Linux, including Ubuntu&reg; 20, 22, and 24 LTS,
            SuSE&reg; 12, and Oracle Linux 9
        </Trans>
    ),
    [AGENT_OS_MAC]: () => <Trans>Runs on Apple Silicon Macs.</Trans>,
    [AGENT_OS_DOCKER_MAC]: () => {
        const helpUrl = useHelpUrl('InstallingVirtualnPoints');
        return (
            <Trans>
                Runs on Intel&reg;-based and Apple Silicon Mac platforms running macOS&reg; 10.14 and later operating
                systems. Requires Docker&reg; to be installed.{' '}
                <a target="_blank" rel="noreferrer" href={helpUrl}>
                    See these instructions.
                </a>
            </Trans>
        );
    },
};

export default function AgentDownloadWizardOS({ wizardSelections }) {
    const classes = useStyles();
    const [osSelection, setOsSelection] = useState(wizardSelections.current.os);
    const agentModel = wizardSelections.current.model;

    const handleChange = (e) => {
        wizardSelections.current.os = e.target.value;
        setOsSelection(e.target.value);
    };

    const AgentDescComponent = AGENT_DESCRIPTIONS[osSelection];

    return (
        <div className={classes.root}>
            <TextField select label={fieldLabel} value={osSelection} onChange={handleChange}>
                <MenuItem value={AGENT_OS_WINDOWS}>
                    <Trans>Microsoft&reg; Windows</Trans>
                </MenuItem>
                <MenuItem value={AGENT_OS_LINUX}>
                    <Trans>Linux</Trans>
                </MenuItem>
                {agentModel === AGENT_VIRTUAL_290_MODEL && (
                    <MenuItem value={AGENT_OS_MAC}>
                        <Trans>macOS</Trans>
                    </MenuItem>
                )}
                <MenuItem value={AGENT_OS_DOCKER_MAC}>
                    <Trans>Docker&reg; for macOS&reg; and Linux&reg;</Trans>
                </MenuItem>
            </TextField>

            <Box mt={2} mb={2} width={550}>
                <Typography variant="caption" component={Box}>
                    {i18n._(AGENT_REQUIREMENTS[osSelection][agentModel])}
                </Typography>
                <Typography variant="caption" component={Box} mt={1}>
                    <AgentDescComponent />
                </Typography>
            </Box>
        </div>
    );
}
